import { bookKombiBuchung, getAvailableTimeslots } from '@/api/otbBeratung'

import { CombiTimeslotDto } from '@/types/otbBeratung'

/**
 * Default state for the OTB appointment module
 * @returns {Object} The default state
 */
function defaultOtbAppointmentState() {
	return {
		// Available timeslots for consultation
		availableTimeslots: [],
		// Selected timeslot for booking
		selectedTimeslot: null,
		// Loading states
		isTimeslotsLoading: false,
		isBookingInProgress: false,
		isDeletingAppointment: false,
		// Current booking
		currentBooking: null,
		// Error state
		error: null,
		// Success state
		bookingSuccess: false
	}
}

export default {
	namespaced: true,
	state: () => ({
		...defaultOtbAppointmentState(),
	}),
	actions: {
		/**
		 * Fetch available consultation timeslots based on the current appointment
		 * @param {Object} param0 - Vuex context
		 * @param {Object} param1 - Parameters
		 * @param {string} param1.globalStpId - The global service point ID
		 * @param {number} param1.lvnr - LV number for the user
		 */
		async fetchAvailableTimeslots({ commit, rootState }) {
			try {
				const currentAppointments = rootState.appointment.currentAppointments
				if (!currentAppointments || currentAppointments.length === 0) {
					commit('setError', 'No current appointments found')
					return
				}

				// Use the earliest start time and latest end time from all appointments
				const allStartTimes = currentAppointments.map(apt => new Date(apt.von))
				const allEndTimes = currentAppointments.map(apt => new Date(apt.bis))
				const start = new Date(Math.min(...allStartTimes))
				const end = new Date(Math.max(...allEndTimes))

				// Use the first appointment for other required data
				const firstAppointment = currentAppointments[0]
				const globalStpId = firstAppointment.dienststelleModel.globalStpId
				const lvnr = rootState.user.userAccountInfo?.lv
				const cknr = rootState.user.userAccountInfo?.cknr
				const email = rootState.user.userAccountInfo?.email?.email
				// Merge all snrIds from all appointments and convert to strings
				const topicIds = currentAppointments
					.flatMap(appointment => appointment.snrIds)
					.map(id => String(id))

				commit('setTimeslotsLoading', true)
				commit('clearError')

				const response = await getAvailableTimeslots(globalStpId, start.toISOString(), end.toISOString(), lvnr, cknr, email, topicIds)

				if (response.data && Array.isArray(response.data)) {
					commit('setAvailableTimeslots', response.data)
				} else {
					commit('setAvailableTimeslots', [])
				}
			} catch (error) {
				console.error('Error fetching available timeslots:', error)
				commit('setError', error.response?.data?.message || error.message || 'Failed to fetch available timeslots')
				commit('setAvailableTimeslots', [])
			} finally {
				commit('setTimeslotsLoading', false)
			}
		},

		/**
		 * Book a consultation appointment
		 * @param {Object} param0 - Vuex context
		 */
		async bookConsultation({ commit, rootState, state }) {
			try {
				if (!state.selectedTimeslot) {
					commit('setError', 'No timeslot selected')
					return
				}

				const currentAppointments = rootState.appointment.currentAppointments
				if (!currentAppointments || currentAppointments.length === 0) {
					commit('setError', 'No current appointments found')
					return
				}

				commit('setBookingInProgress', true)
				commit('clearError')
				commit('setBookingSuccess', false)

				// Use the first appointment for extId (they should all be for the same station)
				const firstAppointment = currentAppointments[0]

				// Combine selected timeslot data with the appointment data
				const bookingData = {
					startDateTimeUTC: state.selectedTimeslot.startsAt,
					endDateTimeUTC: state.selectedTimeslot.endsAt,
					extId: firstAppointment.dienststelleModel.globalStpId,
					lvnr: rootState.user.userAccountInfo?.lv,
					// Merge all snrIds from all appointments and convert to strings
					topicIds: currentAppointments
						.flatMap(appointment => appointment.snrIds)
						.map(id => String(id)),
				}
				
				if (rootState.user.userAccountInfo?.cknr) {
					bookingData.cknr = rootState.user.userAccountInfo.cknr
				}

				// Only add phone and email if they have actual values
				if (rootState.user.userAccountInfo?.phone) {
					bookingData.phone = rootState.user.userAccountInfo.phone
				}

				if (rootState.user.userAccountInfo?.email?.email) {
					bookingData.email = rootState.user.userAccountInfo.email.email
				}

				const response = await bookKombiBuchung(bookingData)
				commit('setCurrentBooking', response.data)
				commit('setBookingSuccess', true)
				return response.data
			} catch (error) {
				console.error('Error booking consultation:', error)
				commit('setError', error.response?.data?.message || error.message || 'Failed to book consultation')
				commit('setBookingSuccess', false)
				throw error
			} finally {
				commit('setBookingInProgress', false)
			}
		},

		/**
		 * Clear the OTB appointment state
		 * @param {Object} param0 - Vuex context
		 */
		clearOtbAppointmentState({ commit }) {
			commit('resetOtbAppointmentState')
		},

		/**
		 * Clear any error state
		 * @param {Object} param0 - Vuex context
		 */
		clearError({ commit }) {
			commit('clearError')
		},

		/**
		 * Select a specific timeslot
		 * @param {Object} param0 - Vuex context
		 * @param {CombiTimeslotDto} timeslot - The timeslot to select
		 */
		selectTimeslot({ commit }, timeslot) {
			commit('setSelectedTimeslot', timeslot)
		},

		/**
		 * Clear the selected timeslot
		 * @param {Object} param0 - Vuex context
		 */
		clearSelectedTimeslot({ commit }) {
			commit('clearSelectedTimeslot')
		},

		/**
		 * Delete/cancel an OTB appointment
		 * @param {Object} param0 - Vuex context
		 * @param {string} appointmentId - The ID of the appointment to delete
		 * @returns {Promise} - Promise that resolves when the appointment is deleted
		 */
		async deleteOtbAppointment({ commit }, appointmentId) {
			try {

				commit('setDeletingAppointment', true)
				commit('clearError')

				// Import the cancelAppointment function dynamically to avoid circular dependencies
				const { cancelAppointment } = await import('@/api/otbBeratung')

				// Call the API to cancel the appointment
				const response = await cancelAppointment(appointmentId)

				// Return the response data
				return response.data
			} catch (error) {
				console.error('Error canceling OTB appointment:', error)
				commit('setError', error.response?.data?.message || error.message || 'Failed to cancel appointment')
				throw error
			} finally {
				commit('setDeletingAppointment', false)
			}
		}
	},
	mutations: {
		resetOtbAppointmentState(state) {
			Object.assign(state, defaultOtbAppointmentState())
		},
		setAvailableTimeslots(state, timeslots) {
			state.availableTimeslots = timeslots
		},
		setSelectedTimeslot(state, timeslot) {
			state.selectedTimeslot = timeslot
		},
		clearSelectedTimeslot(state) {
			state.selectedTimeslot = null
		},
		setTimeslotsLoading(state, isLoading) {
			state.isTimeslotsLoading = isLoading
		},
		setBookingInProgress(state, isInProgress) {
			state.isBookingInProgress = isInProgress
		},
		setDeletingAppointment(state, isDeleting) {
			state.isDeletingAppointment = isDeleting
		},
		setCurrentBooking(state, booking) {
			state.currentBooking = booking
		},
		setError(state, error) {
			state.error = error
		},
		clearError(state) {
			state.error = null
		},
		setBookingSuccess(state, success) {
			state.bookingSuccess = success
		}
	},
	getters: {
		availableTimeslots(state) {
			return state.availableTimeslots
		},
		selectedTimeslot(state) {
			return state.selectedTimeslot
		},
		isTimeslotsLoading(state) {
			return state.isTimeslotsLoading
		},
		isBookingInProgress(state) {
			return state.isBookingInProgress
		},
		isDeletingAppointment(state) {
			return state.isDeletingAppointment
		},
		currentBooking(state) {
			return state.currentBooking
		},
		hasError(state) {
			return !!state.error
		},
		error(state) {
			return state.error
		},
		bookingSuccess(state) {
			return state.bookingSuccess
		},
		/**
		 * Group timeslots by date for better UI presentation
		 */
		timeslotsByDate(state) {
			const grouped = {}

			if (state.availableTimeslots && state.availableTimeslots.length) {
				state.availableTimeslots.forEach(slot => {
					// Extract date part only (YYYY-MM-DD)
					const dateKey = slot.startsAt.split('T')[0]

					if (!grouped[dateKey]) {
						grouped[dateKey] = []
					}

					grouped[dateKey].push(slot)
				})
			}

			return grouped
		}
	},
}
